import { useRef, useState } from "react";
import { FileText, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { adminApi, formatApiError } from "@/lib/adminApi";
import { SizeTag } from "@/studio/RoomsEditor";
import { Block, Chips, More, Q, Seg, Text } from "@/studio/intake/bits";

const BED = ["Bed", "Nightstands", "Dresser", "Desk", "Storage", "Lighting", "Other"];
const ITEMS = {
  primary_bedroom: BED, secondary_bedroom: BED,
  living_room: ["Sofa", "Chairs", "Storage", "Lighting", "Other"],
  dining_room: ["Dining table", "Chairs", "Storage", "Lighting", "Other"],
  living_dining_flex: ["Sofa", "Dining table", "Chairs", "Storage", "Lighting", "Other"],
  patio: ["Chairs", "Dining table", "Sofa", "Lighting", "Other"],
};
const ALL = ["Bed", "Nightstands", "Dresser", "Desk", "Sofa", "Chairs", "Dining table", "Storage", "Lighting", "Other"];
const PENDING = [["dimensions", "Dimensions"], ["openings", "Doors / windows"], ["photos", "Photos"], ["floorplan", "Floor plan"]];
export const itemsFor = (type, all) => (all || type === "other" ? ALL : ITEMS[type]);
const fileUrl = (f) => `${process.env.REACT_APP_BACKEND_URL}${f.url}`;

const FtIn = ({ m, k, set, label, tid }) => (
  <div className="st-field"><span className="st-label">{label}</span>
    <div className="st-ftin">
      <input className="st-input" type="number" min="0" placeholder="ft" value={m[`${k}_ft`] ?? ""} onChange={(e) => set({ [`${k}_ft`]: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`${tid}-${k}-ft`} /><span className="st-muted">ft</span>
      <input className="st-input" type="number" min="0" max="11" placeholder="in" value={m[`${k}_in`] ?? ""} onChange={(e) => set({ [`${k}_in`]: e.target.value === "" ? null : Math.min(11, Number(e.target.value)) })} data-testid={`${tid}-${k}-in`} /><span className="st-muted">in</span>
    </div>
  </div>
);

const Files = ({ ctx, room, kind, tid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const list = ctx.files.filter((f) => f.room_id === room.id && f.kind === kind);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Files must be 15 MB or smaller"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("room_id", room.id); fd.append("kind", kind);
    try {
      const { data } = await adminApi.post(`/studio/projects/${ctx.projectId}/files`, fd);
      ctx.setFiles((fs) => [...fs, data]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Upload failed"));
    } finally {
      setBusy(false); ref.current.value = "";
    }
  };
  const remove = async (f) => { await adminApi.delete(`/studio/files/${f.id}`); ctx.setFiles((fs) => fs.filter((x) => x.id !== f.id)); };
  return (
    <div className="st-thumbs" data-testid={`${tid}-${kind}s`}>
      {list.map((f) => (
        <div className="st-thumb" key={f.id}>
          {f.content_type === "application/pdf" ? <a href={fileUrl(f)} target="_blank" rel="noreferrer" style={{ display: "grid", placeItems: "center" }}><FileText size={22} />PDF</a> : <img src={fileUrl(f)} alt={f.original_name} />}
          <button onClick={() => remove(f)} aria-label="Delete file" data-testid={`${tid}-delete-${f.id}`}><X size={12} /></button>
        </div>
      ))}
      <button type="button" className="st-thumb" style={{ cursor: "pointer", borderStyle: "dashed" }} onClick={() => ref.current.click()} disabled={busy} data-testid={`${tid}-upload-${kind}`}>
        <span style={{ display: "grid", placeItems: "center", gap: 2 }}><Upload size={16} />{busy ? "Uploading…" : kind === "photo" ? "Add photo" : "Floor plan"}</span>
      </button>
      <input ref={ref} type="file" hidden accept="image/jpeg,image/png,image/webp,application/pdf" onChange={(e) => upload(e.target.files[0])} data-testid={`${tid}-file-input-${kind}`} />
    </div>
  );
};

export const areaOf = (m) => {
  const l = (m.length_ft || 0) + (m.length_in || 0) / 12;
  const w = (m.width_ft || 0) + (m.width_in || 0) / 12;
  return l && w ? Math.round(l * w * 10) / 10 : null;
};

export const StepMeasure = ({ ctx }) => (
  <div>
    <p className="st-ask">Let's capture each room as it is today. Anything you can't measure now, mark pending.</p>
    {ctx.rooms.map((r, i) => {
      const m = ctx.measure(r.id);
      const set = (patch) => ctx.setMeasure(r.id, patch);
      const area = areaOf(m);
      const tid = `s2-room-${i}`;
      const openings = m.openings || [];
      return (
        <Block key={r.id} title={r.label} meta={<SizeTag status={m.confirmed && area ? "confirmed" : r.size_status === "confirmed" ? "confirmed" : r.size_sqft ? "estimated" : "unknown"} />} defaultOpen={i === 0} testId={tid}>
          <div className="st-dims">
            <FtIn m={m} k="length" set={set} label="Length" tid={tid} />
            <FtIn m={m} k="width" set={set} label="Width" tid={tid} />
            <FtIn m={m} k="ceiling" set={set} label="Ceiling height" tid={tid} />
          </div>
          <div className="st-toggle-row" style={{ borderTop: 0, paddingBottom: 0 }}>
            <span className="st-small">{area ? <>≈ <strong>{area} sq ft</strong> from L × W</> : <span className="st-muted">Enter length and width to calculate the area</span>}</span>
            <label style={{ display: "flex", gap: 10, alignItems: "center" }} className="st-small">Measurements confirmed
              <Switch checked={!!m.confirmed} disabled={!area} onCheckedChange={(v) => set({ confirmed: v })} data-testid={`${tid}-confirmed`} /></label>
          </div>
          <Q label="Important door & window measurements">
            {openings.map((o, j) => (
              <div key={j} style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 32px", gap: 8, marginBottom: 8 }}>
                <input className="st-input" placeholder="e.g. Window" value={o.label} onChange={(e) => set({ openings: openings.map((x, k) => (k === j ? { ...x, label: e.target.value } : x)) })} data-testid={`${tid}-opening-${j}-label`} />
                <input className="st-input" placeholder="Width" value={o.width} onChange={(e) => set({ openings: openings.map((x, k) => (k === j ? { ...x, width: e.target.value } : x)) })} data-testid={`${tid}-opening-${j}-width`} />
                <input className="st-input" placeholder="Height" value={o.height} onChange={(e) => set({ openings: openings.map((x, k) => (k === j ? { ...x, height: e.target.value } : x)) })} data-testid={`${tid}-opening-${j}-height`} />
                <button className="st-icon-btn" onClick={() => set({ openings: openings.filter((_, k) => k !== j) })} aria-label="Remove" data-testid={`${tid}-opening-${j}-remove`}><X size={14} /></button>
              </div>
            ))}
            <button className="st-more" style={{ paddingTop: 0 }} onClick={() => set({ openings: [...openings, { label: "", width: "", height: "" }] })} data-testid={`${tid}-add-opening`}>+ Add door or window</button>
          </Q>
          <Q label="Photos"><Files ctx={ctx} room={r} kind="photo" tid={tid} /></Q>
          <Q label="Floor plan"><Files ctx={ctx} room={r} kind="floorplan" tid={tid} /></Q>
          <Q label="Mark as pending"><Chips options={PENDING.map(([, l]) => l)} value={(m.pending || []).map((p) => PENDING.find(([k]) => k === p)[1])}
            onChange={(v) => set({ pending: PENDING.filter(([, l]) => v.includes(l)).map(([k]) => k) })} testId={`${tid}-pending`} /></Q>
          <More label="Notes" testId={`${tid}-notes-more`}><Text area value={m.notes} onChange={(v) => set({ notes: v })} placeholder="Measurement notes" testId={`${tid}-notes`} /></More>
        </Block>
      );
    })}
  </div>
);

export const StepFurniture = ({ ctx }) => (
  <div>
    <p className="st-ask">For each room, what stays, what goes, and what's missing?</p>
    {ctx.rooms.map((r, i) => {
      const a = ctx.ra(r.id);
      const set = (patch) => ctx.setRa(r.id, patch);
      const items = itemsFor(r.type, a.show_all);
      const f = a.furniture || {};
      const tid = `s2-furn-${i}`;
      return (
        <Block key={r.id} title={r.label} meta={`${Object.keys(f).length} of ${items.length} set`} defaultOpen={i === 0} testId={tid}>
          <div className="st-furn">
            {items.map((it) => (
              <div key={it} style={{ display: "contents" }}>
                <span className="st-small">{it}</span>
                <Seg options={[["keep", "Keep"], ["replace", "Replace"], ["add", "Add"], ["none", "Not needed"]]} value={f[it]} onChange={(v) => set({ furniture: { ...f, [it]: v } })} testId={`${tid}-${it.toLowerCase().replace(/\s+/g, "-")}`} />
              </div>
            ))}
          </div>
          {r.type !== "other" && <button className="st-more" onClick={() => set({ show_all: !a.show_all })} data-testid={`${tid}-show-all`}>{a.show_all ? "− Show relevant items only" : "+ Show all items"}</button>}
          <More label="Needs & requirements" testId={`${tid}-more`}>
            <Q label="Additional functional needs"><Text value={a.needs} onChange={(v) => set({ needs: v })} testId={`${tid}-needs`} /></Q>
            <Q label="Specific furniture requirements"><Text value={a.requirements} onChange={(v) => set({ requirements: v })} placeholder="e.g. King bed, sofa under 90 in" testId={`${tid}-requirements`} /></Q>
            <Q label="Final style preferences or restrictions"><Text value={a.style_notes} onChange={(v) => set({ style_notes: v })} testId={`${tid}-style-notes`} /></Q>
          </More>
        </Block>
      );
    })}
  </div>
);

const SERVICES = [["assembly", "Interested in recommended furniture assembly services?"], ["shopping", "Interested in personal shopping or accent styling?"], ["photoshoot", "Open to a final project photoshoot and possible marketing use?"]];

export const StepFinish = ({ ctx, missing, jump }) => {
  const s = ctx.pa.services || {};
  return (
    <div>
      <p className="st-ask">A few optional extras, then a quick check of anything still open.</p>
      <div className="st-panel" style={{ padding: "6px 22px" }}>
        {SERVICES.map(([k, l]) => (
          <div className="st-toggle-row" key={k}><span>{l}</span><Switch checked={!!s[k]} onCheckedChange={(v) => ctx.setPa({ services: { ...s, [k]: v } })} data-testid={`s2-service-${k}`} /></div>
        ))}
      </div>
      <p className="st-note">Expressions of interest only — not purchases or photography consent.</p>
      <Q label="Missing / pending items">
        {missing.length === 0 ? <p className="st-muted st-small" data-testid="s2-missing-none">Nothing outstanding.</p> : (
          <ul className="st-list st-check" data-testid="s2-missing-list">
            {missing.map((m) => <li key={m.text}><span>{m.text}</span><button className="st-btn st-btn--quiet st-btn--sm" onClick={() => jump(m.step)} data-testid={`s2-missing-jump-${m.step}`}>Go to {m.title}</button></li>)}
          </ul>
        )}
      </Q>
    </div>
  );
};
